import { collection, doc, getDoc, getDocs } from 'firebase/firestore'
import { db } from '../firebase'

/**
 * القيم المالية المجمّدة للطلبات.
 *
 * التطبيق و Cloud Functions يكتبان هذه القيم مرة واحدة لحظة قبول العرض في
 * `orders/{requestId}`. هذا الملف هو المصدر الوحيد الذي تقرأ منه التقارير،
 * حتى لا يؤدي تعديل `settings/commission_rates` لاحقاً إلى إعادة حساب
 * أرقام الفواتير والتقارير السابقة.
 *
 * القيم القديمة (قبل إطلاق نظام اللقطة) غير مجمّدة، لذا نُرجع
 * `isFrozen: false` ليقرر المستدعي ما يفعله بدل أن يفترض أنها صحيحة.
 */
export interface OrderFinancial {
  requestId: string
  offerId?: string
  buyerId?: string
  sellerId?: string
  offerPrice: number
  commissionRate: number | null
  commissionAmount: number | null
  sellerNetAmount: number | null
  isFrozen: boolean
}

function toNumber(value: unknown): number | null {
  if (value == null) return null
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : null
}

function readFinancial(docId: string, data: Record<string, unknown>): OrderFinancial {
  const snap =
    data.financialSnapshot && typeof data.financialSnapshot === 'object'
      ? (data.financialSnapshot as Record<string, unknown>)
      : {}

  const rate = toNumber(snap.commissionRate) ?? toNumber(data.commissionRate)
  const amount =
    toNumber(snap.commissionAmount) ?? toNumber(data.commissionAmount)
  const price =
    toNumber(snap.offerPrice) ?? toNumber(data.offerPrice) ?? 0

  return {
    requestId: String(data.requestId ?? docId),
    offerId: data.offerId ? String(data.offerId) : undefined,
    buyerId: data.buyerId ? String(data.buyerId) : undefined,
    sellerId: data.sellerId ? String(data.sellerId) : undefined,
    offerPrice: price,
    commissionRate: rate,
    commissionAmount: amount,
    sellerNetAmount:
      toNumber(snap.sellerNetAmount) ?? toNumber(data.sellerNetAmount),
    isFrozen: rate != null,
  }
}

/**
 * يُرجع خريطة requestId → القيم المجمّدة.
 * الطلبات غير المجمّدة (القديمة) غير موجودة في الخريطة.
 */
export async function loadOrderFinancials(): Promise<Map<string, OrderFinancial>> {
  const out = new Map<string, OrderFinancial>()
  try {
    const snapshot = await getDocs(collection(db, 'orders'))
    snapshot.forEach((d) => {
      const fin = readFinancial(d.id, d.data() as Record<string, unknown>)
      if (fin.requestId) out.set(fin.requestId, fin)
    })
  } catch (error) {
    console.error('Error loading order financials:', error)
  }
  return out
}

/** لقطة واحدة عند الحاجة (مسار تفصيلي لطلب واحد). */
export async function loadOrderFinancial(
  requestId: string
): Promise<OrderFinancial | null> {
  if (!requestId) return null
  try {
    const snap = await getDoc(doc(db, 'orders', requestId))
    if (!snap.exists()) return null
    return readFinancial(requestId, snap.data() as Record<string, unknown>)
  } catch (error) {
    console.error(`Error loading order financial for ${requestId}:`, error)
    return null
  }
}

/**
 * نسبة العمولة لطلب — من القيمة المجمّدة فقط.
 *
 * لا تقرأ من الإعدادات إطلاقاً. إن لم تكن مجمّدة (طلب قديم) تُرجع null
 * ليتخذ المستدعي قراراً صريحاً بدل عرض رقم متغيّر.
 */
export function frozenCommissionRate(
  fin: OrderFinancial | null | undefined
): number | null {
  if (!fin || !fin.isFrozen) return null
  return fin.commissionRate
}

/** مبلغ العمولة المجمّد، أو null إن لم تكن مجمّدة. */
export function frozenCommissionAmount(
  fin: OrderFinancial | null | undefined
): number | null {
  if (!fin || !fin.isFrozen) return null
  if (fin.commissionAmount != null) return fin.commissionAmount
  if (fin.commissionRate != null) return fin.offerPrice * fin.commissionRate
  return null
}
